import {
  Modal, Input, Select, Radio, Flex, Typography,
} from 'antd';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AlumlyButton from '../../../components/AlumlyButton';
import { ServiceNameEnum } from '../../../enums/serviceName';
import { ValidationRuleEnum } from '../../../enums/validationRule';
import useServices from '../../../hooks/useServices';
import { AxiosService } from '../../../utils/axiosService';
import ConfirmCreateBoUserModal, { BoUserT } from './modalConfirmCreateBoUser';
import { RoleT } from './FormEditBoUser';

interface ModalCreateBoUserProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const labelStyle: React.CSSProperties = {
  fontSize: '0.875rem',
};
const errorSpanStyle: React.CSSProperties = {
  position: 'absolute', bottom: -20, left: 0, color: 'red', fontSize: '0.875rem',
};
const helperSpanStyle: React.CSSProperties = {
  position: 'absolute', bottom: -20, left: 0, color: 'gray', fontSize: '0.875rem',
};

const ModalCreateBoUser: React.FC<ModalCreateBoUserProps> = (props) => {
  const { open, onClose, onSuccess } = props;

  const [roleOptions, setRoleOptions] = useState<RoleT[]>([]);
  const [isOpenDialog, setIsOpenDialog] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState<boolean>(false);
  const [boUser, setBoUser] = useState<BoUserT>();
  const [status, setStatus] = useState(true);

  const {
    control, setError, formState: { errors }, handleSubmit, reset, setValue,
  } = useForm({
    defaultValues: {
      username: '',
      password: '',
      email: '',
      roles: [{
        id: '',
        group: '',
        code: '',
        displayName: '',
      }],
      firstName: '',
      lastName: '',
      isActive: true,
      status: true,
    },
  });

  const { service: getAllBoRoleService } = useServices(ServiceNameEnum.boRole.index);

  const onChangeRole = (roles: RoleT[]): void => {
    setValue('roles', roles);
  };

  const getRoles = async (): Promise<void> => {
    const { data, error } = await getAllBoRoleService.get();
    if (data) {
      setRoleOptions(data);
      onChangeRole([data?.[0]]);
    } else if (error) {
      const errorResponse = AxiosService.getErrorResponse(error);
      toast.error(errorResponse?.message, { toastId: 'allToast' });
    }
  };

  const onCloseModal = (): void => {
    reset();
    onClose();
  };

  const handleChangeStatus = (newStatus: boolean): void => {
    setStatus(newStatus);
  };

  const onSubmit = (formData: any): void => {
    if (formData.email && formData.password) {
      const {
        password,
        email,
        roles,
        firstName,
        lastName,
      } = formData;
      setBoUser({
        password,
        email,
        roleIds: (roles as RoleT[] || []).map((role) => role.id),
        firstName,
        lastName,
        status,
      });
      setOpenConfirmModal(!openConfirmModal);
      setIsOpenDialog(!isOpenDialog);
    } else {
      setIsOpenDialog(true);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>):void => {
    if (event.key === ' ') {
      event.preventDefault();
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>):void => {
    const pastedText = event.clipboardData.getData('text/plain');
    if (pastedText.includes(' ')) {
      event.preventDefault();
    }
  };

  useEffect(() => {
    onChangeRole([]);
    if (open) {
      getRoles();
    }
  }, [open]);

  const roleError = !!errors?.roles && !(errors?.roles as any)?.ref?.value?.length;

  return (
    <Modal
      open={open}
      onCancel={onCloseModal}
      footer={null}
      width={800}
      title={(
        <div style={{ textAlign: 'center', fontSize: '1.25rem', fontWeight: 'bold' }}>
          Create admin user
        </div>
      )}
    >
      <div style={{ padding: 8 }}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          noValidate
          style={{ marginTop: 16, width: '100%' }}
        >
          <div style={{ marginRight: 24 }}>
            <span style={labelStyle}>Status</span>
            <div>
              <Radio.Group
                value={status}
                onChange={(e) => handleChangeStatus(e.target.value)}
                optionType="button"
                buttonStyle="solid"
              >
                <Radio.Button value style={{ height: 48, lineHeight: '46px' }}>Active</Radio.Button>
                <Radio.Button value={false} style={{ height: 48, lineHeight: '46px' }}>Inactive</Radio.Button>
              </Radio.Group>
            </div>
          </div>
          <div style={{ display: 'flex', marginTop: 8 }}>
            <span style={labelStyle}>Email</span>
            <span style={{ ...labelStyle, color: 'red' }}>*</span>
          </div>
          <div style={{ display: 'flex', position: 'relative' }}>
            <Controller
              name="email"
              control={control}
              rules={{
                required: ValidationRuleEnum.email.required.message,
                pattern: ValidationRuleEnum.email.pattern,
              }}
              render={({ field, fieldState }) => (
                <Input
                  type="email"
                  size="large"
                  placeholder="Enter email"
                  status={fieldState.error ? 'error' : ''}
                  autoFocus
                  {...field}
                />
              )}
            />
            {errors.email && (
            <Typography.Text style={errorSpanStyle}>
              {errors.email?.message}
            </Typography.Text>
            )}
          </div>
          <div style={{ marginTop: 24, position: 'relative' }}>
            <div style={{ display: 'flex' }}>
              <span style={labelStyle}>Password</span>
              <span style={{ ...labelStyle, color: 'red' }}>*</span>
            </div>
            <Controller
              name="password"
              control={control}
              rules={{
                required: ValidationRuleEnum.password.required.message,
                minLength: ValidationRuleEnum.password.minLength,
              }}
              render={({ field, fieldState }) => (
                <Input.Password
                  size="large"
                  onKeyDown={handleKeyDown}
                  onPaste={handlePaste}
                  placeholder="Enter password"
                  status={fieldState.error ? 'error' : ''}
                  {...field}
                />
              )}
            />
            {errors.password ? (
              <Typography.Text style={errorSpanStyle}>
                { errors.password?.message }
              </Typography.Text>
            ) : (
              <Typography.Text style={helperSpanStyle}>
                { ValidationRuleEnum.password.common.message }
              </Typography.Text>
            )}
          </div>
          <div style={{ marginTop: 32 }}>
            <span style={labelStyle}>First name</span>

            <Controller
              name="firstName"
              control={control}
              render={({ field }) => (
                <Input
                  size="large"
                  placeholder="Enter first name"
                  {...field}
                />
              )}
            />
          </div>
          <div style={{ marginTop: 24 }}>
            <span style={labelStyle}>Last name</span>
            <Controller
              name="lastName"
              control={control}
              render={({ field }) => (
                <Input
                  size="large"
                  placeholder="Enter last name"
                  {...field}
                />
              )}
            />
          </div>

          <div style={{ marginTop: 32, position: 'relative' }}>
            <div style={{ display: 'flex' }}>
              <span style={labelStyle}>Role</span>
              <span style={{ ...labelStyle, color: 'red' }}>*</span>
            </div>
            <Controller
              name="roles"
              control={control}
              rules={{ required: ValidationRuleEnum.role.required.message }}
              render={({ field: { value } }) => (
                <Select
                  mode="multiple"
                  size="large"
                  style={{ width: '100%' }}
                  status={roleError ? 'error' : ''}
                  value={(value as RoleT[] || []).map((r) => r.id)}
                  onChange={(_, option) => {
                    const opts = Array.isArray(option) ? option : [option];
                    onChangeRole(opts.map((o: any) => o.role));
                  }}
                  options={roleOptions.map((option) => ({
                    value: option.id,
                    label: option.displayName ?? '-',
                    role: option,
                  }))}
                />
              )}
            />
            {roleError && (
            <Typography.Text style={errorSpanStyle}>
              { ValidationRuleEnum.role.required.message }
            </Typography.Text>
            )}
          </div>

          <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
            <Flex gap={32}>
              <AlumlyButton label="rxvWhite" width={100} onClick={onCloseModal}>
                <span>Cancel</span>
              </AlumlyButton>
              <AlumlyButton label="rxv" width={100} type="submit">
                <span>Create</span>
              </AlumlyButton>
            </Flex>
          </div>
        </form>
      </div>
      <ConfirmCreateBoUserModal
        isOpen={openConfirmModal}
        boUser={boUser!}
        handleClose={() => setOpenConfirmModal(false)}
        handleDialogClose={() => onCloseModal()}
        onSuccess={() => onSuccess()}
      />
    </Modal>
  );
};
export default ModalCreateBoUser;
